import { FC, useCallback, useMemo } from 'react'
import { TextFieldElement, CheckboxElement, PasswordElement, useForm, AutocompleteElement } from 'react-hook-form-mui'
import { zodResolver } from '@hookform/resolvers/zod'
import z from 'zod'
import { Typography } from '@mui/material'
import { LoadingButton } from '@mui/lab'

import Title from './Title'
import usePageErrorMessages from '../hooks/usePageErrorMessages'
import useHiddenFormElement from '../hooks/useHiddenFormElement'
import useIsNavigatingAway from '../hooks/useIsNavigatingAway'
import useFormAnalytics from '../hooks/useFormAnalytics'
import FormContainer from './FormContainer'

interface Fields {
  username: string
  password: string
  title: string
  titleOther?: string
  firstName: string
  lastName: string
  mailingListPreference: boolean
}

const TITLE_CHOICES: String[] = ['Mr', 'Mrs', 'Ms', 'Dr', 'Other']

const SignUpForm: FC = () => {
  const signUpSchema = z.object({
    username: z.string().trim().email(),
    password: z.string().regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9])[\S]{8,16}$/, 'Invalid password'),
    title: z
      .string()
      .trim()
      .max(12, 'Input exceeds the maximum allowed length of 12 characters.')
      .optional()
      .or(z.null().transform(() => undefined))
      .or(z.literal('').transform(() => undefined)),
    titleOther: z
      .string()
      .trim()
      .max(12, 'Input exceeds the maximum allowed length of 12 characters.')
      .optional()
      .or(z.literal('').transform(() => undefined)),
    firstName: z
      .string()
      .trim()
      .min(1, 'This field is required')
      .max(64, 'Input exceeds the maximum allowed length of 64 characters.'),
    lastName: z
      .string()
      .trim()
      .min(1, 'This field is required')
      .max(64, 'Input exceeds the maximum allowed length of 64 characters.'),
    mailingListPreference: z.boolean(),
  })

  const formContext = useForm<Fields>({
    resolver: zodResolver(signUpSchema),
    defaultValues: {
      username: document.querySelector<HTMLInputElement>('#readOnlyEmail')?.value ?? '',
      mailingListPreference: false,
    },
  })
  const {
    formState: { errors },
    watch,
  } = formContext

  const analytics = useFormAnalytics('sign_up')
  const isTitleOtherSelected = watch('title') === 'Other'
  const onSubmit = useCallback(
    ({ password, title, firstName, lastName, mailingListPreference, titleOther }: Fields) => {
      const isOtherEmpty = !titleOther
      if (!title || (isTitleOtherSelected && isOtherEmpty)) {
        const titleElement = document.querySelector<HTMLInputElement>('#extension_TitleOnSignUp')!
        const titleListItemEl = titleElement.parentNode?.parentNode
        titleListItemEl?.parentNode?.removeChild(titleListItemEl)
      } else {
        document.querySelector<HTMLInputElement>('#extension_TitleOnSignUp')!.value =
          isTitleOtherSelected && titleOther ? titleOther : title
      }
      document.querySelector<HTMLInputElement>('#signUpPassword')!.value = password
      document.querySelector<HTMLInputElement>('#extension_FirstNameOnSignUp')!.value = firstName
      document.querySelector<HTMLInputElement>('#extension_LastNameOnSignUp')!.value = lastName
      document.querySelector<HTMLInputElement>('#mailingListPreferenceOnSignUpText_true')!.checked =
        mailingListPreference ? true : false
      analytics.onSubmit()
      document.querySelector<HTMLButtonElement>('#continue')!.click()
    },
    [analytics, isTitleOtherSelected],
  )

  const isNavigatingAway = useIsNavigatingAway()
  const simpleModalPlaceholder = useHiddenFormElement('#simplemodal-placeholder')
  const simpleModalOverlay = useHiddenFormElement('#simplemodal-overlay')
  const simpleModalContainer = useHiddenFormElement('#simplemodal-container')
  const isLoading = useMemo(
    () =>
      isNavigatingAway ||
      simpleModalPlaceholder !== null ||
      simpleModalOverlay !== null ||
      simpleModalContainer !== null,
    [isNavigatingAway, simpleModalPlaceholder, simpleModalOverlay, simpleModalContainer],
  )

  const pageErrorMessages = usePageErrorMessages()

  return (
    <FormContainer formContext={formContext} onSuccess={onSubmit} onError={analytics.onError} onBlur={analytics.onBlur}>
      <Title mb={6}>Sign up</Title>
      <Typography component="div" mb={4}>
        Add a password and your name
      </Typography>
      <TextFieldElement
        fullWidth
        type="email"
        name="username"
        label="Email Address"
        autoComplete="username"
        required
        disabled
      />
      <PasswordElement
        autoFocus
        name="password"
        label="Password"
        autoComplete="new-password"
        required
        fullWidth
        disabled={isLoading}
        parseError={() => ''}
      />
      <Typography variant="caption" mt={1} mb={2} color={errors.password ? 'error' : undefined}>
        8-16 characters, with at least 1 uppercase letter, 1 number &amp; 1 symbol
      </Typography>
      <AutocompleteElement
        autocompleteProps={{
          autoHighlight: true,
          disabled: isLoading,
        }}
        textFieldProps={{
          inputProps: {
            type: 'text',
          },
        }}
        label="Title"
        options={TITLE_CHOICES}
        name="title"
      />
      <TextFieldElement
        id="titleOther"
        fullWidth
        disabled={isLoading}
        label="Title Other"
        name="titleOther"
        style={{ display: isTitleOtherSelected ? 'block' : 'none' }}
      />
      <TextFieldElement fullWidth type="text" name="firstName" label="First Name" required disabled={isLoading} />
      <TextFieldElement fullWidth type="text" name="lastName" label="Last Name" required disabled={isLoading} />
      <CheckboxElement
        name="mailingListPreference"
        label={
          <Typography variant="caption">
            Tick this box if you would like to receive emails from Phillips about our upcoming auctions, exhibitions and
            special events
          </Typography>
        }
        disabled={isLoading}
      />
      {pageErrorMessages && (
        <Typography component="div" my={2} color="error" dangerouslySetInnerHTML={{ __html: pageErrorMessages }} />
      )}
      <LoadingButton fullWidth sx={{ mt: 6 }} type="submit" variant="contained" loading={isLoading}>
        Sign Up
      </LoadingButton>
    </FormContainer>
  )
}

export default SignUpForm
